import { forwardRef, type ButtonHTMLAttributes, type CSSProperties } from 'react'

/**
 * The one close control for the game. Every close button draws an upright plus-sign cross (a crossword or Greek
 * cross: equal arms, not rotated) instead of an X, at twice the old size so it is easy to tap on touch screens and
 * in Tesla mode. Callers pass the sizes the old X used and this doubles them, so the doubling lives in one place.
 */
export const CLOSE_SCALE = 2

/** The plus-sign cross glyph. `size` is the final pixel size (already doubled by CloseButton). */
export function CrossGlyph({ size = 32, strokeWidth = 2.5, className }: { size?: number; strokeWidth?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth}
      strokeLinecap="square" aria-hidden="true" focusable="false" data-glyph="close-cross" className={className}>
      <path d="M12 4v16M4 12h16" />
    </svg>
  )
}

export interface CloseButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Accessible name. Defaults to "Close". */
  label?: string
  /** The old glyph size in px; drawn at CLOSE_SCALE times this. */
  glyph?: number
  /** The old square tap target in px; drawn at CLOSE_SCALE times this. Leave out when CSS sizes the button. */
  box?: number
  strokeWidth?: number
}

export const CloseButton = forwardRef<HTMLButtonElement, CloseButtonProps>(function CloseButton(
  { label = 'Close', glyph = 16, box, strokeWidth, className = '', style, type = 'button', ...rest }, ref,
) {
  const size: CSSProperties | undefined = box ? { width: box * CLOSE_SCALE, height: box * CLOSE_SCALE } : undefined
  return (
    <button ref={ref} type={type} aria-label={label} data-close-cross=""
      className={`inline-flex shrink-0 items-center justify-center ${className}`} style={{ ...size, ...style }} {...rest}>
      <CrossGlyph size={glyph * CLOSE_SCALE} strokeWidth={strokeWidth} />
    </button>
  )
})
